// src/api.js
//
// Single HTTP client for all authenticated API calls.
// Always use baseURL "/api" (nginx proxy in production, dev proxy in npm start).
// Do NOT import axios directly in src/api/* modules — use this default export.
//
import axios from "axios";
import { isLicenseInvalidError } from "./utils/apiError";
import { AUTH_NOTICE_KEY } from "./utils/sessionManager";
import i18n from "./i18n";

const API = axios.create({
  baseURL: "/api",
});

// Helper to clear all auth data (avoiding circular dependency with auth.js)
function clearAllAuthData() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  localStorage.removeItem("role"); // legacy
  localStorage.removeItem("activeRole"); // selected role
  localStorage.removeItem("roles"); // cached roles from /me

  // Destroy session tracking if sessionManager exists
  try {
    const sessionManager = require("./utils/sessionManager").default;
    if (sessionManager && typeof sessionManager.destroy === "function") {
      sessionManager.destroy();
    }
  } catch (e) {
    // sessionManager might not be available, ignore
  }
}

// ✅ Attach JWT before every request
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  const lang = String(i18n.language || 'en').slice(0, 2).toLowerCase();
  config.headers['Accept-Language'] = lang;
  return config;
});

/** Consecutive transport failures before showing the sticky banner. */
const BACKEND_DOWN_STREAK = 3;
let networkFailStreak = 0;

function isTransportFailure(error) {
  if (!error || error.code === "ERR_CANCELED" || error.name === "CanceledError") {
    return false;
  }
  // Explicit network / CORS / offline — not HTTP 4xx/5xx (those have response)
  if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
    return true;
  }
  // Some proxies abort without a response body; ignore if request was aborted
  if (!error.response && error.code !== "ECONNABORTED") {
    // Axios timeout sets ECONNABORTED — treat as soft failure too for banner purposes
    return true;
  }
  if (!error.response && error.code === "ECONNABORTED") {
    return true;
  }
  return false;
}

function clearBackendDownBanner() {
  networkFailStreak = 0;
  const banner = document.getElementById("backend-down-banner");
  if (banner) {
    banner.remove();
  }
  if (document.body.style.paddingTop === "64px") {
    document.body.style.paddingTop = "";
  }
  backendDownBannerShown = false;
}

function noteTransportFailure() {
  networkFailStreak += 1;
  if (networkFailStreak >= BACKEND_DOWN_STREAK) {
    showBackendDownBanner();
  }
}

// ✅ HANDLE EXPIRED / INVALID TOKEN GLOBALLY
API.interceptors.response.use(
  (response) => {
    clearBackendDownBanner();
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const message =
      error?.response?.data?.message || error?.response?.data?.error || "";

    if (isTransportFailure(error)) {
      noteTransportFailure();
      return Promise.reject(error);
    }

    // Got an HTTP response — backend is reachable
    clearBackendDownBanner();

    // 🔴 LICENSE INVALID — redirect to the license page
    // Do NOT redirect plan-limit responses (also 402) — those should be shown inline.
    if (isLicenseInvalidError(error)) {
      if (!window.location.pathname.includes("/license-invalid")) {
        window.location.href = "/license-invalid";
      }
      return Promise.reject(error);
    }

    const isAuthError =
      status === 401 ||
      (status === 403 &&
        (message.toLowerCase().includes("jwt") ||
          message.toLowerCase().includes("token") ||
          message.toLowerCase().includes("authentication"))) ||
      message.toLowerCase().includes("bad credentials") ||
      message.toLowerCase().includes("full authentication is required");

    if (isAuthError) {
      // Store i18n keys; Login translates. Prefer stable keys over backend English.
      const notice =
        status === 401
          ? "auth.sessionExpired"
          : "auth.sessionInvalid";
      sessionStorage.setItem(AUTH_NOTICE_KEY, notice);

      // Clear all auth data including roles, activeRole, and session tracking
      clearAllAuthData();

      if (!window.location.pathname.includes("/login")) {
        window.location.replace("/login");
      }
    }

    return Promise.reject(error);
  },
);

// 🔴 Backend Down Banner
let backendDownBannerShown = false;
function showBackendDownBanner() {
  if (backendDownBannerShown) return;
  backendDownBannerShown = true;

  // Create banner
  const banner = document.createElement("div");
  banner.id = "backend-down-banner";
  banner.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
    color: white;
    padding: 16px 24px;
    text-align: center;
    font-size: 16px;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    z-index: 99999;
    animation: slideDown 0.4s ease-out;
  `;
  banner.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: center; gap: 12px;">
      <svg style="width: 24px; height: 24px;" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
      </svg>
      <span>${i18n.t('common.backendDown')}</span>
    </div>
  `;

  // Add animation
  if (!document.getElementById("backend-down-banner-style")) {
    const style = document.createElement("style");
    style.id = "backend-down-banner-style";
    style.textContent = `
      @keyframes slideDown {
        from { transform: translateY(-100%); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
    `;
    document.head.appendChild(style);
  }
  document.body.prepend(banner);

  // Adjust body padding to account for banner
  document.body.style.paddingTop = "64px";
}

export default API;
